import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { SqueezyImagesCanvas } from "@/components/sections/squeezy-images-canvas"

export interface CarouselImageItem {
	image: string
	title: string
	text: string
	imageAlt?: string
	href?: string
}

interface CarouselImageProps {
	eyebrow?: string
	items: CarouselImageItem[]
}

const MOBILE_CARD_WIDTH = "calc(100vw - 3rem)"
const MOBILE_GUTTER = "1rem"

function usePrefersReducedMotion(): boolean {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)")
		const update = (): void => setPrefersReducedMotion(media.matches)
		update()
		media.addEventListener("change", update)
		return () => media.removeEventListener("change", update)
	}, [])

	return prefersReducedMotion
}

function mobileSnapClass(index: number, total: number): string {
	if (total <= 1) return "snap-center"
	if (index === 0) return "snap-start"
	if (index === total - 1) return "snap-end"
	return "snap-center"
}

interface ProductTitleLinkProps {
	title: string
	href?: string
	className?: string
	id?: string
	as?: "h2" | "p"
}

function ProductTitleLink({ title, href, className, id, as: Tag = "p" }: ProductTitleLinkProps): React.JSX.Element {
	return (
		<div className="flex min-w-0 items-center gap-2">
			<Tag id={id} className={cn("min-w-0 text-pretty", className)}>
				{title}
			</Tag>
			{href && (
				<Button asChild variant="ghost" size="icon-sm" className="size-11 shrink-0 text-primary">
					<Link to={href} aria-label={`Ver ${title}`}>
						<ArrowUpRight className="size-4" />
					</Link>
				</Button>
			)}
		</div>
	)
}

/**
 * Canvas interactivo en desktop (sm+) y tarjetas con scroll-snap en móvil.
 */
export function CarouselImage({ eyebrow, items }: CarouselImageProps): React.JSX.Element {
	const headingId = useId()
	const sectionRef = useRef<HTMLDivElement>(null)
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const canvasEngineRef = useRef<SqueezyImagesCanvas | null>(null)
	const [activeIndex, setActiveIndex] = useState(0)
	const prefersReducedMotion = usePrefersReducedMotion()

	const total = items.length
	const activeItem = items[activeIndex]

	const handleCanvasColumnClick = useCallback(
		(steps: number): void => {
			canvasEngineRef.current?.gotoNext(steps)
			setActiveIndex(current => (current + steps) % total)
		},
		[total]
	)

	const handlePrevClick = (): void => {
		canvasEngineRef.current?.gotoPrev(1)
		setActiveIndex(current => (current - 1 + total) % total)
	}

	const handleNextClick = (): void => {
		canvasEngineRef.current?.gotoNext(1)
		setActiveIndex(current => (current + 1) % total)
	}

	const handleKeyDown = (event: React.KeyboardEvent): void => {
		if (event.key === "ArrowLeft") {
			handlePrevClick()
		}
		if (event.key === "ArrowRight") {
			handleNextClick()
		}
	}

	useEffect(() => {
		const canvas = canvasRef.current
		const section = sectionRef.current
		if (!canvas || !section || total === 0) return

		let disposed = false
		let engine: SqueezyImagesCanvas | null = null

		const observer = new IntersectionObserver(
			entries => {
				const isVisible = entries.some(entry => entry.isIntersecting)
				if (!isVisible || engine || disposed) return

				engine = new SqueezyImagesCanvas({
					canvasElement: canvas,
					items: items.map(item => ({ imgSrc: item.image })),
					onNextColumnClick: handleCanvasColumnClick,
					prefersReducedMotion
				})
				engine.loadCanvasImages()
				canvasEngineRef.current = engine
			},
			{ root: null, threshold: 0, rootMargin: "3000px 0px 3000px 0px" }
		)

		observer.observe(section)

		const resizeObserver = new ResizeObserver(() => {
			canvasEngineRef.current?.onCanvasResize()
		})
		resizeObserver.observe(canvas)

		return () => {
			disposed = true
			observer.disconnect()
			resizeObserver.disconnect()
			engine?.dispose()
			canvasEngineRef.current = null
		}
	}, [handleCanvasColumnClick, items, prefersReducedMotion, total])

	return (
		<section className="bg-background" aria-labelledby={activeItem ? headingId : undefined}>
			<Container className="pb-16 md:pb-24">
				{eyebrow && (
					<Eyebrow align="center" className="mb-8 sm:mb-10">
						{eyebrow}
					</Eyebrow>
				)}

				{activeItem && (
					<header className="mb-8 hidden items-end justify-between gap-6 sm:mb-10 sm:flex">
						<div
							className="min-w-0 flex flex-col gap-2 motion-safe:transition-opacity motion-safe:duration-150 motion-safe:ease-out"
							onKeyDown={handleKeyDown}
						>
							<ProductTitleLink
								id={headingId}
								as="h2"
								title={activeItem.title}
								href={activeItem.href}
								className="max-w-[43ch] text-2xl font-semibold tracking-tight sm:text-3xl"
							/>
							<p className="text-pretty text-lg text-muted-foreground">{activeItem.text}.</p>
						</div>

						{total > 1 && (
							<div className="flex shrink-0 gap-1" role="group" aria-label="Navegación del carrusel">
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-11"
									aria-label="Diapositiva anterior"
									onClick={handlePrevClick}
								>
									<ChevronLeft className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="icon"
									className="size-11"
									aria-label="Diapositiva siguiente"
									onClick={handleNextClick}
								>
									<ChevronRight className="size-4" />
								</Button>
							</div>
						)}
					</header>
				)}

				{/* Desktop: carrusel squeezy con canvas */}
				<div ref={sectionRef} className="hidden sm:block">
					<canvas ref={canvasRef} className="aspect-1232/460 w-full max-w-none" aria-hidden />

					<div aria-live="polite" aria-atomic className="sr-only">
						Elemento {activeIndex + 1} de {total}: {activeItem?.title}
					</div>
				</div>

				{/* Móvil: tarjetas con scroll-snap y peek lateral */}
				<div className="sm:hidden">
					<ul
						role="list"
						className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 scrollbar-none"
						style={{
							paddingInline: MOBILE_GUTTER,
							scrollPaddingInline: MOBILE_GUTTER
						}}
					>
						{items.map((item, index) => (
							<li
								key={index}
								className={cn("shrink-0", mobileSnapClass(index, total))}
								style={
									{
										width: MOBILE_CARD_WIDTH,
										"--carousel-item-index": index
									} as React.CSSProperties
								}
							>
								<article className="flex flex-col gap-4">
									<div className="relative aspect-336/350 overflow-hidden rounded-card bg-muted">
										<img
											src={item.image}
											alt={item.imageAlt ?? ""}
											loading={index === 0 ? "eager" : "lazy"}
											className="absolute inset-0 h-full w-full object-cover"
										/>
									</div>
									<div className="space-y-2">
										<ProductTitleLink
											title={item.title}
											href={item.href}
											className="text-lg font-semibold leading-snug"
										/>
										<p className="text-lg text-muted-foreground">{item.text}</p>
									</div>
								</article>
							</li>
						))}
					</ul>
				</div>
			</Container>
		</section>
	)
}
